import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuthSession } from "../../../lib/auth/session";
import { setRuntimeOfflineEnabled } from "../../../lib/offline/runtime";
import { setOfflineDbNamespace } from "../../../lib/offline/db";

type OfflineModeSnapshot = {
  enabled: boolean;
  lastUpdated: number | null;
};

type OfflineModeContextValue = {
  enabled: boolean;
  lastUpdated: number | null;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
};

const STORAGE_NAMESPACE = "phmis.offlineMode.preference";

const defaultSnapshot: OfflineModeSnapshot = {
  enabled: false,
  lastUpdated: null,
};

const OfflineModeContext = createContext<OfflineModeContextValue | undefined>(undefined);

const buildStorageKey = (userId: string | null | undefined): string =>
  userId ? `${STORAGE_NAMESPACE}.${userId}` : `${STORAGE_NAMESPACE}.anonymous`;

const readStoredPreference = (key: string): OfflineModeSnapshot => {
  if (typeof window === "undefined") {
    return defaultSnapshot;
  }
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) {
      return defaultSnapshot;
    }
    const parsed = JSON.parse(raw) as Partial<OfflineModeSnapshot>;
    if (!parsed || typeof parsed !== "object") {
      return defaultSnapshot;
    }
    return {
      enabled: Boolean(parsed.enabled),
      lastUpdated: typeof parsed.lastUpdated === "number" ? parsed.lastUpdated : null,
    };
  } catch (error) {
    console.warn("Failed to read offline mode preference from storage", error);
    return defaultSnapshot;
  }
};

const persistPreference = (key: string, snapshot: OfflineModeSnapshot) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(snapshot));
  } catch (error) {
    console.warn("Failed to persist offline mode preference", error);
  }
};

export const OfflineModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const session = useAuthSession();
  const userIdentifier =
    session.officerId ||
    session.profile?.userId ||
    session.profile?.staffNumber ||
    session.profile?.email ||
    null;

  const storageKey = useMemo(() => buildStorageKey(userIdentifier), [userIdentifier]);

  const [snapshot, setSnapshot] = useState<OfflineModeSnapshot>(() => readStoredPreference(storageKey));

  useEffect(() => {
    setSnapshot(readStoredPreference(storageKey));
  }, [storageKey]);

  useEffect(() => {
    setOfflineDbNamespace(userIdentifier);
  }, [userIdentifier]);

  useEffect(() => {
    setRuntimeOfflineEnabled(snapshot.enabled);
  }, [snapshot.enabled]);

  const setEnabled = useCallback(
    (enabled: boolean) => {
      setSnapshot((previous) => {
        if (previous.enabled === enabled) {
          return previous;
        }
        const next: OfflineModeSnapshot = {
          enabled,
          lastUpdated: Date.now(),
        };
        persistPreference(storageKey, next);
        return next;
      });
    },
    [storageKey],
  );

  const toggle = useCallback(() => {
    setSnapshot((previous) => {
      const next: OfflineModeSnapshot = {
        enabled: !previous.enabled,
        lastUpdated: Date.now(),
      };
      persistPreference(storageKey, next);
      return next;
    });
  }, [storageKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }
    const handleStorage = (event: StorageEvent) => {
      if (event.key === storageKey) {
        setSnapshot(readStoredPreference(storageKey));
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => {
      window.removeEventListener("storage", handleStorage);
    };
  }, [storageKey]);

  const value = useMemo<OfflineModeContextValue>(
    () => ({
      enabled: snapshot.enabled,
      lastUpdated: snapshot.lastUpdated,
      setEnabled,
      toggle,
    }),
    [snapshot.enabled, snapshot.lastUpdated, setEnabled, toggle],
  );

  return <OfflineModeContext.Provider value={value}>{children}</OfflineModeContext.Provider>;
};

export const useOfflineMode = (): OfflineModeContextValue => {
  const context = useContext(OfflineModeContext);
  if (!context) {
    throw new Error("useOfflineMode must be used within an OfflineModeProvider");
  }
  return context;
};

export const getStoredOfflineModePreference = (userId?: string | null): OfflineModeSnapshot =>
  readStoredPreference(buildStorageKey(userId ?? null));
